import React, { useState } from "react";
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useEa } from "@/components/energy-analytics/analytics/EaContext";
import { useEaData } from "@/components/energy-analytics/analytics/useEaData";
import { EaChartCard, EaNote, EaPageHeader, EaSeg, inputCls } from "@/components/energy-analytics/analytics/EaUi";
import { EaExportButton, EaPointPicker, EaTable, TOT, useSelectedCodes } from "@/components/energy-analytics/analytics/EaControls";
import { COLORS, INDEX_NOTE, MONTHS_FULL, MONTHS_IT, fmtEur, fmtPower, fmtQty, missingMsg, pointSub } from "@/components/energy-analytics/analytics/eaFormat";
import { PageState } from "./EaPowerOverview";

export const SERIES_COLORS = ["#8B32FB", "#3285FD", "#F59E0B", "#16A34A", "#DC2626", "#0891B2", "#DB2777", "#65A30D"];
const axis = { tick: { fontSize: 11, fill: "#64748B" }, axisLine: false, tickLine: false };

// Indice base 100: ogni serie divisa per la propria media.
export function toIndex(arr) {
  const v = arr.filter((x) => x != null);
  const m = v.length ? v.reduce((s, x) => s + x, 0) / v.length : null;
  return arr.map((x) => (x == null || !m ? null : (x / m) * 100));
}

function SeriesChart({ points, field, labels, mode, unitFmt, testid }) {
  const series = points.map((p) => (mode === "index" ? toIndex(p[field]) : p[field]));
  const data = labels.map((l, i) => Object.fromEntries([["label", l], ...points.map((p, j) => [p.identifier, series[j][i]])]));
  return (
    <div className="h-72" data-testid={testid}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
          <XAxis dataKey="label" {...axis} />
          <YAxis {...axis} width={64} tickFormatter={(v) => fmtQty(v, 0)} />
          <Tooltip formatter={(v, n) => [mode === "index" ? fmtQty(v, 1) : unitFmt(v), `POD ${n}`]} />
          <Legend wrapperStyle={{ fontSize: 12 }} formatter={(v) => `POD ${v}`} />
          {points.map((p, j) => <Line key={p.code} dataKey={p.identifier} stroke={SERIES_COLORS[j % SERIES_COLORS.length]} strokeWidth={2} dot={false} connectNulls={false} />)}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function EaPowerComparePoints() {
  const { filters, setFilter, unit } = useEa();
  const codes = useSelectedCodes("POWER");
  const month = filters.month || "";
  const [mode, setMode] = useState("abs");
  const { data: d, loading, error } = useEaData("/ea/power/compare/points", { codes, month });
  const pts = d?.points || [];
  const avg = (k) => { const v = pts.map((p) => p[k]).filter((x) => x != null); return v.length ? v.reduce((s, x) => s + x, 0) / v.length : null; };
  const idx = (k) => (r) => { const a = avg(k); return r[k] != null && a ? fmtQty((r[k] / a) * 100, 0) : "—"; };
  const metricCols = [
    ["consumption", "Consumo", (v) => fmtPower(v, unit)], ["spend", "Spesa fatturata", (v) => fmtEur(v)], ["avg_cost", "Costo medio €/MWh", (v) => fmtQty(v, 2)],
    ["pun_weighted", "PUN pond. €/MWh", (v) => fmtQty(v, 2)], ["peak_kw", "Picco kW", (v) => fmtQty(v, 0)], ["baseload_kw", "Baseload kW", (v) => fmtQty(v, 0)],
  ];
  const cols = [{ key: "identifier", label: "POD", align: "left", render: (r) => <div><div className="font-semibold text-slate-900">{r.identifier}</div><div className="text-xs text-slate-500">{pointSub(r)}</div></div> },
    ...metricCols.map(([k, l, f]) => (mode === "index" ? { key: k, label: `${l.split(" ")[0]} (indice)`, render: idx(k) }
      : { key: k, label: l, fmt: f, total: ["consumption", "spend"].includes(k) ? "sum" : ["avg_cost", "pun_weighted"].includes(k) ? TOT.wavg(k, "consumption") : undefined }))];
  const header = (
    <EaPageHeader title="Confronto POD" subtitle="Seleziona 2 o più POD. Indice base 100 = valore rispetto alla media (dei POD nella tabella, della serie stessa nei grafici)."
      right={<div className="flex gap-2 flex-wrap">
        <select value={month} onChange={(e) => setFilter({ month: e.target.value })} className={inputCls} data-testid="ea-cmp-month">
          <option value="">Tutto l'anno</option>{MONTHS_FULL.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}</select>
        <EaPointPicker commodity="POWER" />
        <EaSeg value={mode} onChange={setMode} options={[["abs", "Valori assoluti"], ["index", "Indice base 100"]]} testid="ea-cmp-mode" />
      </div>} />
  );
  return (
    <PageState header={header} loading={loading} error={error} data={d} testid="ea-power-compare-points">
      {d && <>
        {pts.length < 2 && <EaNote testid="ea-cmp-one">È disponibile un solo POD: il confronto diventa utile con 2 o più punti.</EaNote>}
        <div className="flex justify-end">
          <EaExportButton filename={`power_confronto_pod_${d.year}${month ? `_${month}` : ""}`} commodity="POWER" year={d.year}
            period={month ? `${MONTHS_FULL[month - 1]} ${d.year}` : `Anno ${d.year}`} testid="ea-cmp-export"
            columns={metricCols.map(([k, l]) => ({ key: k, label: k === "consumption" ? "Consumo kWh" : l }))} rows={pts} />
        </div>
        {pts.some((p) => p.consumption == null) && <EaNote testid="ea-cmp-missing">{missingMsg(pts.filter((p) => p.consumption == null).map((p) => `POD ${p.identifier}`), month ? `mese di ${MONTHS_FULL[month - 1].toLowerCase()} ${d.year}` : `${d.year}`)}</EaNote>}
        <EaTable columns={cols} rows={pts} testid="ea-cmp-table" />
        {mode === "index" && <EaNote testid="ea-cmp-index-note">{INDEX_NOTE}</EaNote>}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <EaChartCard title="Consumo mensile" subtitle={mode === "index" ? "Indice base 100 sulla media dell'anno" : "kWh a consuntivo"} testid="ea-cmp-card-monthly">
            <SeriesChart points={pts} field="monthly_consumption" labels={MONTHS_IT} mode={mode} unitFmt={(v) => fmtPower(v, unit)} testid="ea-cmp-chart-monthly" />
          </EaChartCard>
          <EaChartCard title="Profilo orario medio" subtitle={mode === "index" ? "Indice base 100 sulla media giornaliera" : "kWh medi per ora"} testid="ea-cmp-card-hourly">
            <SeriesChart points={pts} field="hourly_profile" labels={Array.from({ length: 24 }, (_, h) => `${String(h).padStart(2, "0")}`)} mode={mode} unitFmt={(v) => fmtQty(v, 0, "kWh")} testid="ea-cmp-chart-hourly" />
          </EaChartCard>
        </div>
      </>}
    </PageState>
  );
}
